/**
 * FAQ — premium multi-color accordion
 * ---------------------------------------------------------------------------
 * Each Q gets its own accent. Opened item glows softly; the number badge
 * carries the colour so the eye tracks quickly.
 */
import React, { useState } from 'react';
import { Plus, Minus, HelpCircle } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const FAQ_COLORS = ['#00ff9d', '#4de0ff', '#c084fc', '#ffd34d', '#ff2d92', '#ff7a3d', '#25d366'];

const FAQ = ({ limit }) => {
  const { config } = useSiteConfig();
  const data = limit ? (config.faqs || []).slice(0, limit) : (config.faqs || []);
  const [open, setOpen] = useState(0);
  if (data.length === 0) return null;

  return (
    <section className="relative py-16 sm:py-22 overflow-hidden">
      {/* Ambient blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-20 left-1/4 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#4de0ff' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 right-1/4 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#c084fc' }} />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12">
          <div className="eh-kicker justify-center mb-3">// FAQ_DB</div>
          <h2 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(1.7rem, 5vw, 3.2rem)' }}>
            FREQUENTLY <span className="eh-neon">ASKED</span>
          </h2>
          <p className="opacity-75 mt-3 max-w-md mx-auto text-sm leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            Straight answers. No legalese. If your question isn&apos;t here, DM us — humans reply.
          </p>
        </div>

        <div className="space-y-2.5 sm:space-y-3">
          {data.map((f, idx) => {
            const isOpen = open === idx;
            const c = FAQ_COLORS[idx % FAQ_COLORS.length];
            return (
              <div
                key={idx}
                className="faq-tile relative rounded-2xl border-2 overflow-hidden"
                style={{
                  borderColor: isOpen ? c : `${c}30`,
                  background: isOpen
                    ? `linear-gradient(160deg, ${c}12 0%, transparent 65%)`
                    : `linear-gradient(160deg, ${c}05 0%, transparent 65%)`,
                  boxShadow: isOpen ? `0 20px 55px -25px ${c}80` : 'none',
                  '--c': c,
                }}
                data-testid={`faq-row-${idx}`}
              >
                {isOpen && <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${c}, transparent)` }} />}
                <button
                  onClick={() => setOpen(o => (o === idx ? -1 : idx))}
                  className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-9 h-9 rounded-lg grid place-items-center shrink-0 eh-mono text-[11px] font-black tracking-widest"
                      style={{
                        background: `${c}18`,
                        border: `1px solid ${c}55`,
                        color: c,
                      }}
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="font-bold text-[14px] sm:text-[15px] leading-snug min-w-0" style={{ fontFamily: 'Inter,sans-serif' }}>
                      {f.q}
                    </span>
                  </div>
                  <span
                    className="w-8 h-8 rounded-full grid place-items-center shrink-0 transition-all"
                    style={{
                      background: isOpen ? c : 'transparent',
                      border: `1px solid ${c}${isOpen ? '' : '55'}`,
                      color: isOpen ? '#04120a' : c,
                    }}
                  >
                    {isOpen ? <Minus size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
                  </span>
                </button>
                <div
                  className="grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-out"
                  style={{ gridTemplateRows: isOpen ? '1fr' : '0fr', opacity: isOpen ? 1 : 0 }}
                >
                  <div className="min-h-0">
                    <div
                      className="px-5 sm:px-6 pb-5 pt-1 text-[13.5px] leading-relaxed opacity-85"
                      style={{ fontFamily: 'Inter,sans-serif', borderTop: `1px dashed ${c}33` }}
                    >
                      <div className="pt-4 flex items-start gap-2">
                        <HelpCircle size={14} className="mt-1 shrink-0" style={{ color: c, opacity: 0.7 }} />
                        <span>{f.a}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .faq-tile { transition: border-color .2s, box-shadow .35s, background .3s; }
        .faq-tile:hover { border-color: var(--c) !important; }
      `}</style>
    </section>
  );
};
export default FAQ;
